import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import VerdictBand from './VerdictBand';
import Callouts from './Callouts';
import EvidenceTabs from './EvidenceTabs';
import MethodologyDisclosure from './MethodologyDisclosure';
import { EvidenceLine } from './primitives';
import { primaryAction } from './decisionMapping';

/**
 * Counterparty Opinion — reading order matches brand/opinion-target-state.png:
 *   1. Position + Condition metadata
 *   2. Callouts
 *   3. Why / So What / Now What (commercial levers)
 *   4. Falsifier
 *   5. Supporting evidence
 *   6. Methodology
 */
const CompanyReport = ({ company, useCase = 'vendor', anchorId, defaultEvidenceOpen = false }) => {
  const [evidenceOpen, setEvidenceOpen] = useState(defaultEvidenceOpen);
  const [rationaleOpen, setRationaleOpen] = useState(false);

  const { analysis } = company;
  const { aiAnalysis, distressScore } = analysis;

  const strengths = aiAnalysis.strengths || [];
  const concerns = aiAnalysis.concerns || [];
  const action = primaryAction(distressScore.riskZone, useCase, aiAnalysis);
  const firstNextStep = (aiAnalysis.mitigations?.nextSteps || [])[0];
  const falsifier = aiAnalysis.falsifier;
  const commercial = aiAnalysis.commercialImplications || {};

  const leverRows =
    useCase === 'ma_private'
      ? [
          { key: 'diligenceExposure', label: 'Diligence exposure', value: commercial.diligenceExposure }
        ]
      : useCase === 'investor'
        ? []
        : [
            { key: 'paymentTerms', label: 'Payment terms', value: commercial.paymentTerms },
            { key: 'contractLength', label: 'Contract length', value: commercial.contractLength },
            { key: 'dualSourcing', label: 'Dual-sourcing', value: commercial.dualSourcing },
            { key: 'prepayment', label: 'Prepayment', value: commercial.prepayment }
          ];

  if (aiAnalysis?.declined || aiAnalysis?.outcome === 'declined') {
    return (
      <article className="fr-report" id={anchorId}>
        <VerdictBand company={company} useCase={useCase} billing={company.billing} />
        <MethodologyDisclosure analysis={analysis} />
      </article>
    );
  }

  return (
    <article className="fr-report" id={anchorId}>
      <VerdictBand company={company} useCase={useCase} billing={company.billing} />

      <Callouts analysis={analysis} />

      <div className="fr-brief">
        <div className="fr-brief-col">
          <h4 className="fr-section-heading">What drives it</h4>

          {concerns.length > 0 && (
            <>
              <h5 className="fr-evidence-sub fr-text-medium">Evidence increasing risk</h5>
              <ul className="fr-evidence-list">
                {concerns.map((item, index) => (
                  <li key={`c-${index}`}>
                    <span className="fr-evidence-rank fr-num">{index + 1}</span>
                    <EvidenceLine text={item} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {strengths.length > 0 && (
            <>
              <h5 className="fr-evidence-sub fr-text-low">Evidence reducing risk</h5>
              <ul className="fr-evidence-list">
                {strengths.map((item, index) => (
                  <li key={`s-${index}`}>
                    <span className="fr-evidence-rank fr-num">{index + 1}</span>
                    <EvidenceLine text={item} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {strengths.length === 0 && concerns.length === 0 && (
            <p className="fr-rationale-text">No specific drivers identified for this period.</p>
          )}

          {aiAnalysis.distressAssessment ? (
            <>
              <button
                type="button"
                className="fr-disclosure-toggle"
                style={{ padding: '0.55rem 0 0' }}
                aria-expanded={rationaleOpen}
                onClick={() => setRationaleOpen(!rationaleOpen)}
              >
                <ChevronRight size={14} className="fr-chevron" />
                Full assessment
              </button>
              {rationaleOpen && <p className="fr-rationale-text">{aiAnalysis.distressAssessment}</p>}
            </>
          ) : null}
        </div>

        <div className="fr-brief-col">
          <h4 className="fr-section-heading">For each reader</h4>
          {Array.isArray(aiAnalysis.stakeholderReadings) &&
          aiAnalysis.stakeholderReadings.length > 0 ? (
            <div className="fr-stakeholder-readings">
              <ul className="fr-stakeholder-list">
                {aiAnalysis.stakeholderReadings.map((row) => (
                  <li key={row.stakeholder} className={row.lead ? 'fr-stakeholder-lead' : ''}>
                    <strong>
                      {row.label}
                      {row.lead ? ' (lead)' : ''}
                    </strong>
                    <span>{row.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="fr-nowwhat-action">{action.text}</p>
              {leverRows.length > 0 ? (
                <div className="fr-commercial">
                  <h5 className="fr-evidence-sub">Commercial implications</h5>
                  <ul className="fr-commercial-list">
                    {leverRows.map((row) =>
                      row.value ? (
                        <li key={row.key}>
                          <strong>{row.label}:</strong> {row.value}
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              ) : null}
            </>
          )}

          {firstNextStep ? (
            <div className="fr-nowwhat-row">
              <span>First step</span>
              <span>{firstNextStep}</span>
            </div>
          ) : null}
        </div>
      </div>

      {falsifier?.improving && falsifier?.worsening ? (
        <aside className="fr-falsifier" aria-label="What would change this Opinion">
          <h4 className="fr-falsifier-title">What would change this Opinion</h4>
          <ul className="fr-falsifier-list">
            <li className="fr-falsifier-up">
              <span className="fr-falsifier-mark" aria-hidden="true" />
              {falsifier.improving}
            </li>
            <li className="fr-falsifier-down">
              <span className="fr-falsifier-mark" aria-hidden="true" />
              {falsifier.worsening}
            </li>
          </ul>
        </aside>
      ) : null}

      <div>
        <button
          type="button"
          className="fr-disclosure-toggle"
          aria-expanded={evidenceOpen}
          onClick={() => setEvidenceOpen(!evidenceOpen)}
        >
          <ChevronRight size={14} className="fr-chevron" />
          Supporting evidence
          <span className="fr-disclosure-summary">
            Multi-year trend, scores &amp; ratios, monitoring
            {aiAnalysis.mitigations ? ', actions' : ''}
          </span>
        </button>
        {evidenceOpen && (
          <div className="fr-disclosure-body">
            <EvidenceTabs company={company} />
          </div>
        )}
      </div>

      <MethodologyDisclosure company={company} />
    </article>
  );
};

export default CompanyReport;
